import { useState } from 'react';
import { c } from '../../theme';
import { fmtMoney, fmtNumber, fmtCyds, fmtRate, fmtPct, costCenterName } from './format';

// Finance's Sales Status Report Summary, in three widths.
//
// The workbook has 21 columns, and all of them are here under "Full workbook" —
// the people who reconcile against Meher's file need it column for column. But
// opening on 21 columns means a reader scrolls sideways before they have read
// anything, so the default is the handful that answer the weekly question, with
// the streams one click away.
//
// The choice is remembered per browser: somebody who works in the full view
// should not have to re-pick it every visit.
const STORAGE_KEY = 'sales-summary-columns';

const MONEY = { fmt: fmtMoney };
const CYDS  = { fmt: fmtCyds };
const RATE  = { fmt: fmtRate };

const streamColumns = (prefix, label) => [
  { key: `${prefix}_revenue`, head: `${label} $`, ...MONEY },
  { key: `${prefix}_volume`, head: `${label} Cyds`, ...CYDS },
  { key: `${prefix}_rev_per_cyd`, head: `${label} /Cyd`, ...RATE },
];

const COLUMN_SETS = {
  overview: {
    label: 'Overview',
    columns: [
      { key: 'accounts', head: 'Accts', fmt: fmtNumber },
      { key: 'participation_rate', head: '% Part.', render: (r) => <Participation row={r} /> },
      { key: 'revenue', head: 'Revenue', ...MONEY },
      { key: 'volume', head: 'Monthly Cyds', ...CYDS },
      { key: 'rev_per_cyd', head: 'Rev$/Cyd', ...RATE },
    ],
  },
  streams: {
    label: 'Streams',
    columns: [
      ...streamColumns('trash', 'Trash'),
      ...streamColumns('recycle', 'Recycle'),
      ...streamColumns('organics', 'Organics'),
      { key: 'rev_per_cyd', head: 'Total Rev$/Cyd', ...RATE },
    ],
  },
  full: {
    label: 'Full workbook',
    columns: [
      { key: 'accounts', head: 'Total Accts', fmt: fmtNumber },
      { key: 'participating', head: 'Yes', fmt: fmtNumber },
      { key: 'not_participating', head: 'No', fmt: fmtNumber },
      { key: 'participation_rate', head: '% Part.', fmt: (v) => fmtPct(v) },
      ...streamColumns('trash', 'Trash'),
      ...streamColumns('recycle', 'Recycle'),
      ...streamColumns('organics', 'Organics'),
      { key: 'service_revenue', head: 'Service $', ...MONEY },
      { key: 'volume', head: 'Monthly Cyds', ...CYDS },
      { key: 'service_rev_per_cyd', head: 'Service /Cyd', ...RATE },
      { key: 'distance_revenue', head: 'Distance', ...MONEY },
      { key: 'access_revenue', head: 'Access', ...MONEY },
      { key: 'ancillary_revenue', head: 'Total Ancillary', ...MONEY },
      { key: 'revenue', head: 'Total Revenue', ...MONEY },
      { key: 'rev_per_cyd', head: 'Total Rev$/Cyd', ...RATE },
    ],
  },
};

// Two rates share a volume and differ in revenue. The API's rev_per_cyd is the
// workbook's Total Rev$/Cyd — all five materials, ancillary included, over
// service volume, the headline figure ($11.13 on 9/1/26). The service rate
// leaves the ancillary dollars out. Both come from sums, never an average of
// rows.
function derive(row) {
  return {
    ...row,
    not_participating: row.accounts - row.participating,
    service_rev_per_cyd: row.volume ? row.service_revenue / row.volume : null,
  };
}

function Participation({ row }) {
  const rate = row.participation_rate;
  return (
    <span style={s.partCell}>
      <span>{fmtPct(rate)}</span>
      {/* 62% and 77% read as nearly the same number and not at all the same
          bar, which is the comparison this column is for. */}
      <span style={s.meter}><span style={{ ...s.meterFill, width: `${Math.round((rate || 0) * 100)}%` }} /></span>
    </span>
  );
}

function readStored() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return COLUMN_SETS[stored] ? stored : 'overview';
  } catch { return 'overview'; }
}

export default function SalesSummaryTable({ rows, grandTotal, previousByCostCenter, previousLabel, selected, onSelect }) {
  const [setKey, setSetKey] = useState(readStored);
  const columns = COLUMN_SETS[setKey].columns;
  const body = rows.map(derive);
  const total = grandTotal ? derive(grandTotal) : null;

  function choose(key) {
    setSetKey(key);
    try { localStorage.setItem(STORAGE_KEY, key); } catch { /* a preference, not state */ }
  }

  const cell = (col, row) => (col.render ? col.render(row) : col.fmt(row[col.key]));

  return (
    <div>
      <div style={s.controls}>
        <div style={s.hint}>
          {selected ? 'Click the selected row again to go back to all cost centers.' : 'Click a cost center to follow it in the charts below.'}
        </div>
        <div style={s.chips} role="group" aria-label="Columns">
          {Object.entries(COLUMN_SETS).map(([key, def]) => (
            <button
              key={key} type="button" aria-pressed={setKey === key}
              onClick={() => choose(key)}
              style={{ ...s.chip, ...(setKey === key ? s.chipOn : null) }}
            >
              {def.label}
            </button>
          ))}
        </div>
      </div>

      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, ...s.thLeft, ...s.sticky }}>Cost Center</th>
              {columns.map((col) => <th key={col.key + col.head} style={s.th}>{col.head}</th>)}
              {previousLabel && <th style={s.th}>vs {previousLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {body.map((row) => {
              const isSelected = selected === row.cost_center;
              const selectable = row.cost_center !== null;
              return (
                <tr
                  key={row.cost_center ?? 'unresolved'}
                  onClick={selectable ? () => onSelect(isSelected ? null : row.cost_center) : undefined}
                  onKeyDown={selectable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(isSelected ? null : row.cost_center); }
                  } : undefined}
                  tabIndex={selectable ? 0 : undefined}
                  role={selectable ? 'button' : undefined}
                  aria-pressed={selectable ? isSelected : undefined}
                  style={{ ...s.tr, ...(selectable ? s.selectable : null), ...(isSelected ? s.selectedRow : null) }}
                >
                  <td
                    style={{ ...s.ccCell, ...s.sticky, ...(isSelected ? s.stickySelected : null), ...(row.cost_center === null ? s.unresolved : null) }}
                    title={row.cost_center === null
                      ? 'Lines the mapping files could not place in a cost center. Counted in the totals, never assigned to a guessed yard.'
                      : undefined}
                  >
                    {costCenterName(row.cost_center)}
                  </td>
                  {columns.map((col) => <td key={col.key + col.head} style={s.td}>{cell(col, row)}</td>)}
                  {previousLabel && (
                    <td style={s.td}>
                      <Change now={row.rev_per_cyd} before={previousByCostCenter?.get(row.cost_center)} />
                    </td>
                  )}
                </tr>
              );
            })}
            {total && (
              <tr>
                <td style={{ ...s.ccCell, ...s.sticky, ...s.totalCell }}>Grand Total</td>
                {columns.map((col) => (
                  <td key={col.key + col.head} style={{ ...s.td, ...s.totalCell }}>
                    {col.render ? fmtPct(total.participation_rate) : col.fmt(total[col.key])}
                  </td>
                ))}
                {previousLabel && (
                  <td style={{ ...s.td, ...s.totalCell }}>
                    <Change now={total.rev_per_cyd} before={previousByCostCenter?.get(null)} onDark />
                  </td>
                )}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Rev$/Cyd against the previous export, per cost center. Stated as movement,
// not as good or bad: no target has been set for this figure yet.
function Change({ now, before, onDark }) {
  if (before === null || before === undefined || !before || now === null || now === undefined) {
    return <span style={s.changeNone}>—</span>;
  }
  const pct = ((now - before) / Math.abs(before)) * 100;
  if (Math.abs(pct) < 0.05) return <span style={s.changeNone}>no change</span>;
  return (
    <span style={{ ...s.change, ...(onDark ? { color: c.surface } : null) }}>
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

const s = {
  controls: { display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', padding: '0 16px 10px' },
  hint:     { fontSize: '11px', color: c.textFaint },
  chips:    { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  chip:     { height: '28px', fontSize: '11px', fontFamily: 'inherit', padding: '0 10px', borderRadius: '6px', borderWidth: '1px', borderStyle: 'solid', borderColor: c.border, background: c.surface, color: c.textMuted, cursor: 'pointer' },
  chipOn:   { background: c.navy, borderColor: c.navy, color: c.surface, fontWeight: 600 },
  // The table scrolls inside this box so the page never does, and the cost
  // center column stays pinned while it happens.
  scroll:   { overflowX: 'auto' },
  // 11.5px body and 10px uppercase headers, as the Transactions table sets it.
  table:    { width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: c.surface, fontSize: '11.5px' },
  th:       { padding: '8px 10px', background: c.surfaceSunk, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'right' },
  thLeft:   { textAlign: 'left' },
  tr:       {},
  selectable: { cursor: 'pointer' },
  selectedRow: { background: c.surfaceSunk },
  td:       { padding: '7px 10px', textAlign: 'right', color: c.textStrong, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}`, fontVariantNumeric: 'tabular-nums' },
  ccCell:   { padding: '7px 10px', fontWeight: 500, color: c.text, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  sticky:   { position: 'sticky', left: 0, background: c.surface, zIndex: 1 },
  stickySelected: { background: c.surfaceSunk, boxShadow: `inset 3px 0 0 ${c.navy}` },
  unresolved: { color: c.warningText, fontStyle: 'italic' },
  totalCell: { fontWeight: 600, color: c.surface, background: c.navy, borderBottom: 'none', fontVariantNumeric: 'tabular-nums' },
  partCell: { display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' },
  meter:    { display: 'block', width: '46px', height: '4px', background: c.canvas, borderRadius: '2px', overflow: 'hidden' },
  meterFill:{ display: 'block', height: '100%', background: c.navy },
  change:   { fontSize: '11px', fontWeight: 600, color: c.textStrong, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  changeNone: { fontSize: '11px', color: c.textFaint },
};
